import * as React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { CircularProgress } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { toast } from "react-toastify";
import { useConfirm } from "material-ui-confirm";

import { useDialplanEditorContext } from "./dialplan/DialplanEditorContext";
import { usePhonesRepository } from "../repository/PhonesRepository";
import useAuthentication from "../service/AuthenticationService";

export default function CopyDialplanFromPhoneButton() {
  const authentication = useAuthentication();
  const confirm = useConfirm();
  const { dialplan, setDialplan } = useDialplanEditorContext();
  const { data: phones, isLoading, fetchDialplanForPhone } = usePhonesRepository();

  const [open, setOpen] = React.useState(false);
  const [isFetching, setIsFetching] = React.useState(false);
  const [selected, setSelected] = React.useState(null);

  const sourcePhones = (phones || []).filter(
    p => p.hasDialplan && p.uuid !== authentication.phoneUuid
  );

  if (!isLoading && sourcePhones.length === 0) {
    return null;
  }

  const closeModal = () => {
    if (isFetching) return;
    setOpen(false);
    setSelected(null);
  };

  const onConfirm = async () => {
    if (!selected) return;

    try {
      await confirm({
        title: "Sostituire la configurazione attuale?",
        description: `Verrà importato il risponditore di "${selected.name || selected.phoneNumber}". Le modifiche non salvate andranno perse.`,
        cancellationText: "Annulla",
        confirmationText: "Sostituisci",
      });
    } catch {
      return;
    }

    setIsFetching(true);
    try {
      const sourceDialplan = await fetchDialplanForPhone(selected.uuid);
      setDialplan({
        ...dialplan,
        start: sourceDialplan.start,
      });
      setOpen(false);
      setSelected(null);
      toast.success("Configurazione importata. Ricordati di salvare!", {
        position: "bottom-center",
        autoClose: 3000,
      });
    } catch (e) {
      console.error(e);
      toast.error("Non riesco a importare la configurazione. Riprova!", {
        position: "bottom-center",
        autoClose: 3000,
      });
    } finally {
      setIsFetching(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<FileDownloadIcon/>}
        color="secondary"
        onClick={() => setOpen(true)}
        disabled={isLoading}>
        Importa
      </Button>
      <Dialog open={open} onClose={closeModal} fullWidth maxWidth="xs">
        <DialogTitle>Importa configurazione</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Scegli il numero da cui importare la configurazione del risponditore.
          </DialogContentText>
          <Autocomplete
            options={sourcePhones}
            value={selected}
            onChange={(_, value) => setSelected(value)}
            getOptionLabel={(phone) => phone.name ? `${phone.name} (${phone.phoneNumber})` : phone.phoneNumber}
            isOptionEqualToValue={(a, b) => a.uuid === b.uuid}
            disabled={isFetching}
            autoHighlight
            openOnFocus
            renderInput={(params) => (
              <TextField {...params} label="Cerca un numero" autoFocus/>
            )}/>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal} disabled={isFetching}>
            Annulla
          </Button>
          <Button
            onClick={onConfirm}
            disabled={!selected || isFetching}
            endIcon={isFetching ? <CircularProgress size={20}/> : undefined}>
            Conferma
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
